import React, { useState } from 'react';
import { Button, Icon, Loader, Message, Modal } from 'semantic-ui-react';
import {
  downloadGeneralCondition,
  getGeneralConditions,
  GeneralConditionsByInsurer
} from '../../../../../../services/bond-insurance';
import { dangerNotification } from '../../../../../../services/notification';
import {
  DownloadSlot,
  FileIcon,
  FileInfo,
  FileMeta,
  FileRow,
  FileTitle,
  InsurerCard,
  InsurerName,
  Subtitle
} from './styles';

export type GeneralConditionsModalProps = {
  insurers: { code: string; name: string }[];
};

const getFileTitle = (filename: string): string => {
  if (filename.includes('locatario')) {
    return 'Condições gerais - Locatário';
  }
  if (filename.includes('locador')) {
    return 'Condições gerais - Locador';
  }
  return 'Condições gerais';
};

const getExtension = (filename: string): string =>
  filename.split('.').pop()?.toUpperCase() || '';

const GeneralConditionsModal = ({ insurers }: GeneralConditionsModalProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [conditions, setConditions] = useState<GeneralConditionsByInsurer[]>([]);
  const [downloading, setDownloading] = useState<string | null>(null);

  const insurerName = (code: string) =>
    insurers.find(insurer => insurer.code === code)?.name || code;

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      setConditions(await getGeneralConditions(insurers.map(insurer => insurer.code)));
    } catch (e) {
      setConditions([]);
      dangerNotification('Oops...', 'Não foi possível carregar as condições gerais');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (insurerCode: string, filename: string) => {
    const key = `${insurerCode}/${filename}`;
    setDownloading(key);
    try {
      await downloadGeneralCondition(insurerCode, filename);
    } catch (e) {
      dangerNotification('Oops...', 'Ocorreu um erro ao baixar a condição geral');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Modal
      size="small"
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      trigger={
        <Button type="button" basic size="small" style={{ margin: 0 }}>
          <Icon name="file alternate outline" />
          Condições Gerais
        </Button>
      }
    >
      <Modal.Header>
        <Icon name="file alternate outline" style={{ color: '#59249B' }} />
        Condições Gerais
      </Modal.Header>
      <Modal.Content scrolling>
        <Subtitle>
          Documentos das seguradoras desta análise. Clique em um documento para baixar.
        </Subtitle>

        {loading && <Loader active inline="centered" />}

        {!loading && conditions.length === 0 && (
          <Message info content="Nenhuma condição geral disponível para estas seguradoras." />
        )}

        {!loading &&
          conditions.map(condition => (
            <InsurerCard key={condition.insurerCode}>
              <InsurerName>{insurerName(condition.insurerCode)}</InsurerName>
              {condition.files.map(file => {
                const key = `${condition.insurerCode}/${file.filename}`;
                const extension = getExtension(file.filename);
                const isPdf = extension === 'PDF';

                return (
                  <FileRow
                    key={key}
                    type="button"
                    disabled={downloading !== null}
                    onClick={() => handleDownload(condition.insurerCode, file.filename)}
                  >
                    <FileIcon className={isPdf ? 'pdf' : undefined}>
                      <Icon name={isPdf ? 'file pdf outline' : 'file word outline'} />
                    </FileIcon>
                    <FileInfo>
                      <FileTitle>{getFileTitle(file.filename)}</FileTitle>
                      <FileMeta>
                        {extension} · {file.filename}
                      </FileMeta>
                    </FileInfo>
                    <DownloadSlot>
                      {downloading === key ? (
                        <Loader active inline size="tiny" />
                      ) : (
                        <Icon name="download" className="download-icon" />
                      )}
                    </DownloadSlot>
                  </FileRow>
                );
              })}
            </InsurerCard>
          ))}
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" basic onClick={() => setOpen(false)}>
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default GeneralConditionsModal;
